import type { ReactElement } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  AsyncSection,
  asyncStatus,
  Alert,
  StatusPill,
  Button,
  Card,
  CardBody,
  SettingsGroup,
  Skeleton,
} from "@playbookai/ui";
import {
  MailDomainStatusApiError,
  getMailDomainStatus,
  mailDomainStatusQueryKey,
  recheckMailDomain,
  type RecordFinding,
  type RecordVerdict,
} from "../../api/mail-domain-status-api.js";
import { useAuthStore } from "../../auth/auth-store.js";
import { useDateTimeFormatter } from "../../settings/format-date-time.js";
import { AdminTimeZoneLabel } from "./AdminTimeZoneLabel.js";

/* Sender records — SPF, DKIM and DMARC (PB-2938).
 *
 * These three DNS records decide whether a receiving server trusts mail
 * claiming to come from the customer's domain, so they are the answer to the
 * most common support case: "the invitation never arrived."
 *
 * Only a VERIFIED domain is checked. An unverified one is not evidence of
 * ownership, and resolving records for it would let a tenant read the DNS
 * posture of a domain it does not control.
 */

/** Five states, not three. `not_configured` and `could_not_check` are NOT
    failures and must never be coloured as one. */
const TONE: Record<
  RecordVerdict,
  "healthy" | "watch" | "critical" | "neutral"
> = {
  pass: "healthy",
  review: "watch",
  fail: "critical",
  /* Neutral: the administrator has not supplied a DKIM selector, so there is
       nowhere to look. That is a gap in configuration, not a broken record. */
  not_configured: "neutral",
  /* Neutral, emphatically not danger: an unreachable resolver says nothing
       about their records, and colouring it red sends someone to edit DNS that
       is already correct. */
  could_not_check: "neutral",
};

const LABEL: Record<RecordVerdict, string> = {
  pass: "Pass",
  review: "Review",
  fail: "Fail",
  not_configured: "Not configured",
  could_not_check: "Could not check",
};

function RecordRow({
  name,
  finding,
}: {
  name: string;
  finding: RecordFinding;
}): ReactElement {
  return (
    <div className="flex flex-col gap-1 border-t border-pb-border/70 pt-3 first:border-t-0 first:pt-0">
      <div className="flex items-center gap-2">
        <span className="font-medium">{name}</span>
        <StatusPill tone={TONE[finding.verdict]}>
          {LABEL[finding.verdict]}
        </StatusPill>
      </div>
      {/* The consequence, not the raw record: an administrator needs to know
          what happens to their mail, not to read a policy string. */}
      <span className="text-dense-1 text-pb-text-muted">
        {finding.consequence}
      </span>
      {finding.value !== null ? (
        <code className="text-dense-1 break-all">{finding.value}</code>
      ) : null}
    </div>
  );
}

export function SenderRecordsPanel(): ReactElement {
  const formatter = useDateTimeFormatter();
  const { dateTimeOr } = formatter;
  const queryClient = useQueryClient();
  /* Scoped to the tenant so an in-place session replacement cannot render the
     previous tenant's sending domain or its verdicts from cache. */
  const tenantId = useAuthStore((state) => state.claims?.tenantId ?? null);
  const queryKey = mailDomainStatusQueryKey(tenantId);
  const status = useQuery({
    queryKey,
    queryFn: getMailDomainStatus,
    /* Not fetched until the tenant is known. Without this, a mount before
       claims resolve fetches under the `null` key and then again under the real
       one — harmless, but a wasted request and a cache entry nothing reads. */
    enabled: tenantId !== null,
  });
  const recheck = useMutation({
    mutationFn: recheckMailDomain,
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey });
    },
  });

  const entries = status.data ?? [];

  return (
    /* `SettingsGroup`, like the four zones above it on this tab. As a
       `SectionHeader` this rendered an h1 — a SECOND page-level heading inside
       a tab panel — at a type scale nothing else on the tab used, so it read as
       a different kind of thing rather than the fifth zone in one sequence. */
    <SettingsGroup
      separated
      title="Sender records"
      description="How receiving servers judge mail from your sending domain. Checked only for domains this workspace has verified."
    >
      {/* One component owns the four branches. The failure used to be an
          untitled Alert carrying the server's sentence and no way back. */}
      <AsyncSection
        status={asyncStatus({
          isLoading: status.isPending,
          isError: status.isError,
          isEmpty: entries.length === 0,
          hasData: entries.length > 0,
        })}
        loadingLabel="the sender records"
        errorTitle="Couldn't load the sender records"
        errorDescription={(status.error as Error | null)?.message}
        onRetry={() => {
          void status.refetch();
        }}
        skeleton={<Skeleton className="h-24" />}
        empty={
          <Alert tone="info" title="No active mail server">
            Configure and activate a mail server above, and its sending domain
            will be checked here.
          </Alert>
        }
      >
        {/* One disclosure for the whole list. Every card below carries the
            same kind of instant in the same zone, so naming it per card would
            repeat it rather than inform (PB-5409). */}
        <p className="mb-3 text-dense-1 text-pb-text-muted">
          <AdminTimeZoneLabel
            label="Times"
            formatter={formatter}
            variant="inline"
          />
        </p>
        {entries.map((entry) => (
          <Card key={entry.domain} className="mb-3">
            <CardBody className="flex flex-col gap-3">
              <div className="flex flex-wrap items-center justify-between gap-2">
                <span className="font-medium">{entry.domain}</span>
                {entry.verified ? (
                  <Button
                    variant="secondary"
                    onClick={() => recheck.mutate()}
                    disabled={recheck.isPending}
                  >
                    {recheck.isPending ? "Checking..." : "Check now"}
                  </Button>
                ) : null}
              </div>

              {/* Stated, never a blank panel: an empty result reads as
                  "nothing wrong", which is the opposite of the truth. */}
              {entry.reason !== null ? (
                <Alert tone="watch">{entry.reason}</Alert>
              ) : null}

              {entry.findings !== null ? (
                <>
                  <div className="flex flex-col gap-3">
                    <RecordRow name="SPF" finding={entry.findings.spf} />
                    <RecordRow name="DKIM" finding={entry.findings.dkim} />
                    <RecordRow name="DMARC" finding={entry.findings.dmarc} />
                  </div>
                  {/* The gap is stated rather than left for an administrator to
                      assume away. A partial SPF evaluator would report "your
                      relay is not authorised" for records that authorise it via
                      a nested include. */}
                  <span className="text-dense-1 text-pb-text-muted">
                    SPF is checked for presence and syntax. Whether your
                    specific mail server is listed in the record is not
                    evaluated.
                  </span>
                  <span className="text-dense-1 text-pb-text-muted">
                    Last checked {dateTimeOr(entry.lastCheckedAt, "never")}
                  </span>
                </>
              ) : null}
            </CardBody>
          </Card>
        ))}
      </AsyncSection>

      {recheck.isError ? (
        /* An ownership read that did not complete is NOT a fault in their
           configuration, so it must not be red. This panel already renders
           `could_not_check` neutrally for the same reason, and the GET path says
           as much in its `reason` — the POST path was the one place still
           shouting about a condition the copy itself calls harmless. */
        <Alert
          tone={
            (recheck.error as MailDomainStatusApiError).code ===
            "domain_ownership_unavailable"
              ? "info"
              : "critical"
          }
        >
          {(recheck.error as Error).message}
        </Alert>
      ) : null}
    </SettingsGroup>
  );
}
